import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  downloadBalanceSheetXlsx,
  type FinanceStatementResponse,
  getBalanceSheet,
} from "@/lib/api";
import { downloadCsv, statementToCsv } from "./report-csv";
import { sameDayLastYear } from "./report-dates";
import { ReportBackButton } from "./ReportBackButton";
import { ReportError, ReportPeriod, StatementTable } from "./StatementTables";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function BalanceSheetPage() {
  const [asAt, setAsAt] = useState(todayIso);
  const [compare1, setCompare1] = useState("");
  const [compare2, setCompare2] = useState("");
  const [compare3, setCompare3] = useState("");
  const [applied, setApplied] = useState({
    asAt: todayIso(),
    compare: [] as string[],
  });
  const [statement, setStatement] = useState<FinanceStatementResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);

  const run = useCallback((query: { asAt: string; compare: string[] }) => {
    setLoading(true);
    setError(null);
    getBalanceSheet({
      as_at: query.asAt,
      // Repeatable `?compare_date=` — one entry per comparison date (see
      // `buildUrl` in lib/api.ts). A single date sends one param, several
      // send several; each adds a comparative column.
      ...(query.compare.length > 0
        ? {
            compare_date:
              query.compare.length === 1 ? query.compare[0] : query.compare,
          }
        : {}),
    })
      .then(setStatement)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!asAt) {
      setError("As-at date is required.");
      return;
    }
    setApplied({
      asAt,
      compare: [compare1.trim(), compare2.trim(), compare3.trim()].filter(
        (date) => date.length > 0,
      ),
    });
  };

  const exportXlsx = async () => {
    // Dedicated balance-sheet workbook twin
    // (`GET /reports/balance-sheet.xlsx`) — the applied as-at + comparison
    // dates pass straight through.
    setExporting(true);
    setError(null);
    try {
      await downloadBalanceSheetXlsx({
        as_at: applied.asAt,
        ...(applied.compare.length > 0
          ? {
              compare_date:
                applied.compare.length === 1
                  ? applied.compare[0]
                  : applied.compare,
            }
          : {}),
      });
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setExporting(false);
    }
  };

  // Fills the first comparison date with the same date one year earlier so a
  // year-over-year view needs no hand typing.
  const compareToLastYear = () => {
    if (!asAt) {
      setError("Enter an as-at date before comparing.");
      return;
    }
    const lastYear = sameDayLastYear(asAt);
    if (!lastYear) {
      setError("A valid as-at date is needed to compare years.");
      return;
    }
    setCompare1(lastYear);
    setApplied({ asAt, compare: [lastYear] });
  };

  // Client-side CSV of the rows already on screen — no workbook endpoint.
  const exportCsv = () => {
    if (!statement) return;
    setCsvExporting(true);
    try {
      downloadCsv(
        `balance-sheet-${applied.asAt}.csv`,
        statementToCsv(statement),
      );
    } finally {
      setCsvExporting(false);
    }
  };

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Balance sheet</h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Assets, liabilities, and equity on a chosen date — including a Current
            Year Earnings row that keeps the sheet balanced.
          </p>
          {statement ? <ReportPeriod statement={statement} /> : null}
        </div>
        <ReportBackButton />
      </div>

      {error ? (
        <ReportError message={error} onRetry={() => run(applied)} />
      ) : null}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
        <form
          onSubmit={submit}
          aria-label="Balance sheet filters"
          className="surface grid min-w-0 gap-3 rounded-2xl p-5"
        >
          <div className="flex flex-wrap items-end gap-3">
            <label htmlFor="bs-as-at" className="grid gap-1 text-xs font-medium">
              As-at date
              <Input
                id="bs-as-at"
                type="date"
                value={asAt}
                onChange={(e) => setAsAt(e.target.value)}
                required
                disabled={loading}
              />
            </label>
            <Button type="submit" disabled={loading || !asAt}>
              {loading ? "Running..." : "Run report"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={compareToLastYear}
              disabled={loading || !asAt}
              title="Add the same date one year earlier as a comparison column"
            >
              Compare to last year
            </Button>
          </div>
          <details className="rounded-xl border border-[var(--border)] px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-[var(--muted-foreground)]">
              Compare dates (optional)
              {applied.compare.length > 0
                ? ` — ${applied.compare.length} applied`
                : ""}
            </summary>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label
                htmlFor="bs-compare-1"
                className="grid gap-1 text-xs font-medium"
              >
                Compare date 1 (optional)
                <Input
                  id="bs-compare-1"
                  type="date"
                  value={compare1}
                  onChange={(e) => setCompare1(e.target.value)}
                  disabled={loading}
                />
              </label>
              <label
                htmlFor="bs-compare-2"
                className="grid gap-1 text-xs font-medium"
              >
                Compare date 2 (optional)
                <Input
                  id="bs-compare-2"
                  type="date"
                  value={compare2}
                  onChange={(e) => setCompare2(e.target.value)}
                  disabled={loading}
                />
              </label>
              <label
                htmlFor="bs-compare-3"
                className="grid gap-1 text-xs font-medium"
              >
                Compare date 3 (optional)
                <Input
                  id="bs-compare-3"
                  type="date"
                  value={compare3}
                  onChange={(e) => setCompare3(e.target.value)}
                  disabled={loading}
                />
              </label>
            </div>
          </details>
        </form>

        {/* Dedicated balance-sheet workbook twin
            (`GET /reports/balance-sheet.xlsx`) — sits with the filter row,
            not inside it. CSV is built from the rows already fetched. */}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={exportXlsx}
            disabled={exporting || loading}
            title="Download the balance-sheet workbook (.xlsx) for the applied date"
          >
            {exporting ? "Exporting..." : "Export (XLSX)"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={exportCsv}
            disabled={csvExporting || loading || !statement}
            title="Download the balance sheet on screen as a CSV file"
          >
            {csvExporting ? "Exporting..." : "Export (CSV)"}
          </Button>
        </div>
      </div>

      {loading && statement === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : statement ? (
        <div
          className="table-scroll [&>.surface]:w-max [&>.surface]:min-w-full"
          role="region"
          aria-label="Balance sheet statement — scrolls sideways on narrow screens"
          tabIndex={0}
        >
          <StatementTable
            statement={statement}
            emptyMessage="No balances as at this date."
            drillRange={{
              start_date: `${applied.asAt.slice(0, 4)}-01-01`,
              end_date: applied.asAt,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
